<template>
  <teleport
    :disabled="!teleportContent"
    to="body"
  >
    <transition
      v-if="!noContent"
      :name="transitionName"
    >
      <div
        v-cloak
        v-show="isActive"
        ref="overlay"
        :class="[
          $blockClass(blockClass),
          type,
          `${type}--${direction}`,
        ]"
        class="overlay critical-hide"
      >
        <div
          v-if="template"
          ref="scrollElement"
          :class="[
            $blockClass(blockClass, 'container'),
            $blockClass(type, 'container'),
          ]"
          class="overlay__container"
          @scroll="ev => $emit('scroll:update', ev)"
        >
          <div
            :class="[
              $blockClass(blockClass, 'header'),
              $blockClass(type, 'header'),
              { [emptyCartClasses]: applyEmptyClasses || !$slots.headercontent }
            ]"
            class="overlay__header container"
          >
            <div
              v-if="$slots.headercontent || $slots.title"
              :class="[
                $blockClass(blockClass, 'header-content-wrapper'),
                $blockClass(type, 'header-content-wrapper'),
              ]"
              class="overlay__header-content-wrapper"
            >
              <p
                v-if="$slots.title"
                :class="[
                  $blockClass(blockClass, 'title'),
                  $blockClass(type, 'title'),
                ]"
                class="overlay__title h3"
              >
                <slot name="title" />
              </p>

              <div
                v-if="$slots.headercontent"
                :class="[
                  $blockClass(blockClass, 'header-content'),
                  $blockClass(type, 'header-content'),
                ]"
                class="overlay__header-content"
              >
                <slot name="headercontent" />
              </div>
            </div>

            <button
              :aria-label="$string('accessibility.close')"
              :class="[
                $blockClass(blockClass, 'close'),
                $blockClass(type, 'close'),
              ]"
              class="overlay__close icon-button"
              @click.prevent="closeOverlay(namespace)"
            >
              <slot name="close">
                <span v-html="$icon('enclosed.close')" />
              </slot>
            </button>
          </div>

          <div
            :class="[
              $blockClass(blockClass, 'body'),
              $blockClass(type, 'body'),
            ]"
            class="overlay__body"
          >
            <slot
              :cart-drawer-update="handleCartDrawerHeader"
              :is-active="isActive"
              :reset-scroll="resetScroll"
              :scroll-element="scrollElement"
              name="body"
            />
          </div>
        </div>

        <!-- If template prop is false then just render body slot -->
        <div
          v-else
          ref="scrollElement"
          :class="[
            $blockClass(blockClass, 'container'),
            $blockClass(type, 'container'),
          ]"
          class="overlay__container"
        >
          <slot
            :cart-drawer-update="handleCartDrawerHeader"
            :is-active="isActive"
            :reset-scroll="resetScroll"
            :scroll-element="scrollElement"
            name="body"
          />
        </div>
      </div>
    </transition>
  </teleport>

  <teleport
    :disabled="!teleport"
    to="body"
  >
    <transition name="fade">
      <button
        v-if="isActive && showOverlay"
        class="window-overlay"
        :class="namespace"
        tabindex="-1"
        aria-hidden="true"
        @click.prevent="closeOverlay()"
      />
    </transition>
  </teleport>
</template>

<script>

/**
 * Vue: Overlay
 * -----------------------------------------------------------------------------
 * Base template for overlays (drawers and modals).
 * - Add `js-overlay="scrollElement"` to element in <slot> to pass that as the
 *   element to disableBodyScroll instead of `.overlay__container`.
 *
 * @param {Boolean} [autoQueue] - Automatically opened overlays are queued.
 * @param {Number} [autoShow] - Time in ms to wait before auto-showing overlay.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} direction - Open animation direction set using transition
 * styles, `up` or `down` for `modal` and `left`, `right` or `up` for `drawer`.
 * @param {Boolean} [disableScroll] - Trigger `disableBodyScroll` on open.
 * @param {String} namespace - Unique namespace for overlay, reference when
 * opening/closing the overlay.
 * @param {Boolean} [noContent] - Displays no content, only window overlay, you
 * must manually disable scroll and trap focus in the component which triggers
 * this overlay.
 * @param {Boolean} [showOverlay] - Show the window overlay?
 * @param {Boolean} [teleport] - Teleport window overlay to end of <body>.
 * @param {Boolean} [teleportContent] - Teleport content to end of <body>.
 * Useful for overlays being rendered in carousels.
 * @param {Boolean} [template] - Render with default templating.
 * @param {String} type - Type of overlay, `modal` or `drawer`.
 *
 */
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import timings from '~/config/timings'

export default {
  name: 'Overlay',

  props: {
    autoQueue: {
      type: Boolean,
      default: true,
    },
    autoShow: {
      type: Number,
      default: 0,
    },
    blockClass: {
      type: String,
      default: '',
    },
    direction: {
      type: String,
      default: 'up',
      required: true,
    },
    disableScroll: {
      type: Boolean,
      default: true,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    noContent: {
      type: Boolean,
      default: false,
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
    teleport: {
      type: Boolean,
      default: true,
    },
    teleportContent: {
      type: Boolean,
      default: false,
    },
    template: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: 'modal',
      required: true,
    },
  },

  emits: ['scroll:update'],

  data() {
    return {
      global: true,
      scrollElement: false,
      applyEmptyClasses: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getFirstElement: 'accessibility/getFirstElement',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      hydrated: (state) => state.index.hydrated,
      previousElement: (state) => state.accessibility.previousElement,
    }),

    /**
     * Compute overlay active state.
     * @returns {Boolean}
     */
    isActive() {
      return this.getActiveOverlay === this.namespace
    },

    /**
     * Compute transition name based on type.
     * @returns {String}
     */
    transitionName() {
      const type = this.type === 'modal' ? 'fade' : 'slide'
      return `${type}-${this.direction}`
    },

    emptyCartClasses() {
      let string = 'overlay__header--empty'
      if (this.blockClass) {
        string += ` ${this.blockClass}__header--empty`
      }
      return string
    },
  },

  watch: {

    /**
     * Watch hydrated state to start auto-show countdown.
     */
    hydrated() {
      this.setAutoShow()
    },

    /**
     * Watch active state.
     */
    isActive() {
      if (!this.disableScroll || this.noContent) {
        return
      }

      /**
       * If active then:
       * - Set previous element (to focus on close).
       * - Disable body scroll.
       * - Trap focus inside overlay element.
       * - Focus first trap focus element.
       */
      if (this.isActive) {
        this.getScrollElement()
        this.setPreviousElement(document.activeElement)
        disableBodyScroll(this.scrollElement)

        nextTick(() => {
          this.updateFocusTrap()
        })

        return
      }

      if (this.scrollElement) {
        enableBodyScroll(this.scrollElement)
      } else {
        clearAllBodyScrollLocks()
      }

      /**
       * If no other active overlay when closing then clear focus trap.
       */
      if (!this.getActiveOverlay) {
        this.clearFocusTrap()
      }

      /**
       * Set focus back to previous element if set.
       */
      if (!this.previousElement || typeof this.previousElement !== 'object') {
        return
      }

      this.previousElement.focus()
      this.setPreviousElement()
    },
  },

  mounted() {
    this.setEventBusListeners()
    this.setAutoShow()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      queueOverlay: 'overlays/queue',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPreviousElement: 'accessibility/setPreviousElement',
    }),

    /**
     * Get scroll element.
     */
    getScrollElement() {
      this.scrollElement = (
        this.$refs.scrollElement.querySelector('[js-overlay="scrollElement"]') ||
        this.$refs.scrollElement
      )
    },

    resetScroll() {
      this.scrollElement.scrollTop = 0
    },

    /**
     * Set modal to autoshow.
     */
    setAutoShow() {
      if (!this.autoShow || !this.hydrated) {
        return
      }

      setTimeout(() => {
        if (this.autoQueue) {
          this.queueOverlay({ namespace: this.namespace })
          return
        }

        this.openOverlay({ namespace: this.namespace })
      }, this.autoShow)
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('overlay:update', (namespace) => {
        if (!this.isActive || namespace !== this.namespace) {
          return
        }

        this.updateFocusTrap()
        this.updateBodyScroll()
      })
    },

    /**
     * Update focus trap using Vuex..
     */
    updateFocusTrap() {
      this.setFocusTrap({ container: this.$refs.overlay })

      /**
       * Have to use timeout to wait for animation to complete.
       * - Safari doesn't support `preventScroll`.
       */
      setTimeout(() => {
        if (!this.getFirstElement) {
          return
        }

        this.getFirstElement.focus({ preventScroll: true })
      }, timings.m)
    },

    /**
     * Update body scroll element.
     */
    updateBodyScroll() {
      if (this.scrollElement) {
        enableBodyScroll(this.scrollElement)
      }
      else {
        clearAllBodyScrollLocks()
      }

      this.getScrollElement()
      disableBodyScroll(this.scrollElement)
    },

    /**
     * Handle style for overlay header when receiving an event from cart drawer.
     * @param {Object} payload - The data being emitted from cart drawer
     * @param {Boolean} payload.isCartEmpty - Whether cart is empty or not.
     */
    handleCartDrawerHeader(payload) {
      if (!payload) {
        return
      }

      this.applyEmptyClasses = payload.isCartEmpty
    },
  },
}
</script>
